/**
 * AnalyticsConsentNotice — the fleet's first-visit analytics consent notice.
 *
 * Shows once, only to a visitor who has never made an analytics decision
 * (getStoredConsent() === null) and whose browser does not send Global
 * Privacy Control. Fleet-standard wording and equal-weight buttons — change
 * them here, never per app (docs/ANALYTICS_STANDARD.md). "Yes" and "No thanks" both record the
 * decision through setAnalyticsConsent, so the notice never reappears;
 * the close button hides it for this page view without recording anything,
 * so an undecided visitor is asked again next visit. The app's privacy-page
 * toggle remains the change-your-mind control.
 *
 * Wiring contract (see docs/ANALYTICS_CONSENT_ROLLOUT.md):
 *  - Pass onDecision and persist the choice into the app's own preferences
 *    store — the boot path reads the preference, so skipping this makes a
 *    banner "yes" silently revert on the next visit.
 *  - The app's startAnalytics must NOT write a consent state for a
 *    never-asked visitor (guard on getStoredConsent() !== null ||
 *    hasAnalyticsPreference()), or the never-asked state this notice keys
 *    on is erased at first boot.
 *
 * Deliberately dependency-free beyond React: inline styles (the package is
 * outside every app's Tailwind content scan, so utility classes would
 * generate no CSS), no framer-motion, plain <a> for the privacy link.
 */
import React, { useEffect, useState } from 'react';
import { getStoredConsent, isGlobalPrivacyControlOn, setAnalyticsConsent } from './index.js';


// ---------------------------------------------------------------------------
// Contrast helpers (WCAG 2.1 AA — SC 1.4.3).
//
// `accent` is whatever the host app passes in, and across the fleet it ranges
// from amber (#f59e0b) to violet (#8b5cf6). White on the amber measured
// 2.15:1; violet fails BOTH ways (4.23 with white, 4.22 with ink), so no
// single text colour fixes every app and the ground itself has to move.
// Rather than pick one palette and impose it, derive a passing pair from the
// accent the app already chose.
//
// Kept dependency-free and inline, like the rest of this component.
const INK = [15, 23, 42];
const WHITE = [255, 255, 255];
const PANEL = [15, 18, 30]; // the notice's own near-opaque ground

const toRgb = (value) => {
  let h = String(value).trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return [139, 92, 246]; // fall back to the default violet
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const toHex = (rgb) => '#' + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');
const luminance = ([r, g, b]) => {
  const f = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};
const mix = (rgb, target, amount) => rgb.map((c, i) => c + (target[i] - c) * amount);

// A button ground built from the accent that clears 4.5:1 with one of the two
// text colours. Shifts the accent toward black (or white, when dark text is
// the better fit) in small steps until it passes.
const accentButton = (accent) => {
  const base = toRgb(accent);
  const preferWhite = contrast(WHITE, base) >= contrast(INK, base);
  const fg = preferWhite ? WHITE : INK;
  const towards = preferWhite ? [0, 0, 0] : WHITE;
  let ground = base;
  for (let step = 0; step <= 20 && contrast(fg, ground) < 4.5; step += 1) {
    ground = mix(base, towards, step * 0.05);
  }
  return { background: toHex(ground), color: toHex(fg) };
};

// A link tone for the notice's own dark panel. Lightens the accent until it
// clears 4.5:1 there — the violet accents sat at 4.37-4.41.
const accentOnPanel = (accent) => {
  const base = toRgb(accent);
  let tone = base;
  for (let step = 0; step <= 20 && contrast(tone, PANEL) < 4.5; step += 1) {
    tone = mix(base, WHITE, step * 0.05);
  }
  return toHex(tone);
};
// ---------------------------------------------------------------------------

export function AnalyticsConsentNotice({
  appName,
  privacyHref = '/privacy',
  accent = '#8b5cf6',
  onDecision,
}) {
  const [visible, setVisible] = useState(false);
  const choiceButton = accentButton(accent);
  const linkColour = accentOnPanel(accent);

  useEffect(() => {
    // Never ask someone whose browser already says no (Global Privacy Control).
    if (getStoredConsent() !== null || isGlobalPrivacyControlOn()) return undefined;
    // Small delay so the notice never races the app's first paint.
    const timer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  if (!visible) return null;

  const decide = (granted) => {
    setAnalyticsConsent(granted);
    if (onDecision) onDecision(granted);
    setVisible(false);
  };

  // Tap targets: 44px, the fleet's minimum (WCAG 2.2 SC 2.5.8 sets 24px;
  // Apple and Android guidance, and the fleet, use 44). They were 39px tall,
  // and the close button 23x26 — under even the WCAG floor.
  const buttonBase = {
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: 600,
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    border: '1px solid transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <aside
      role="dialog"
      aria-label="Usage analytics"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '1rem',
        zIndex: 9999,
        width: 'calc(100% - 2rem)',
        maxWidth: '34rem',
        background: 'rgba(15, 18, 30, 0.96)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        borderRadius: '0.75rem',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
        color: '#e2e8f0',
        padding: '1rem 1.25rem',
        backdropFilter: 'blur(12px)',
        fontSize: '0.9rem',
        lineHeight: 1.5,
      }}
    >
      <button
        type="button"
        aria-label="Not now"
        onClick={() => setVisible(false)}
        style={{
          position: 'absolute',
          top: '0.125rem',
          right: '0.125rem',
          width: '44px',
          height: '44px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          borderRadius: '0.5rem',
          color: 'rgba(226, 232, 240, 0.75)',
          fontSize: '1.1rem',
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
      {/* Fleet-standard wording (docs/ANALYTICS_STANDARD.md). GA4 keeps a
          pseudonymous client ID, so this never says "anonymous". */}
      <p style={{ margin: '0 2.5rem 0.75rem 0' }}>
        <strong style={{ color: '#ffffff' }}>Help improve {appName}?</strong>{' '}
        With your OK, Google Analytics counts which pages and features get
        used. It keeps a random ID in a cookie on this device, never sees what
        you write, and is never used for ads. Nothing is sent unless you say
        yes, and you can change your mind any time on the{' '}
        <a
          href={privacyHref}
          style={{ color: linkColour, textDecoration: 'underline' }}
        >
          Privacy page
        </a>
        .
      </p>
      {/* Equal weight: saying no must be as easy and as prominent as saying yes. */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {[
          { label: "Yes, that's fine", granted: true },
          { label: 'No thanks', granted: false },
        ].map(({ label, granted }) => (
          <button
            key={label}
            type="button"
            onClick={() => decide(granted)}
            style={{
              ...buttonBase,
              background: choiceButton.background,
              color: choiceButton.color,
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </aside>
  );
}
